import { create } from '@bufbuild/protobuf'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { getAreaWeatherActual } from '@ebalance/proto/bffuser/v1/weather_actual_service-WeatherActualService_connectquery'
import {
  GetAreaWeatherActualRequestSchema,
  type GetAreaWeatherActualResponse,
} from '@ebalance/proto/bffuser/v1/weather_actual_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { WEATHER_REFETCH_INTERVAL_MS } from '~/hooks/weather/refetchInterval'

/** useAreaWeatherActual のパラメータ。 */
export type UseAreaWeatherActualParams = {
  areaCode: AreaCode
  targetDateFrom: ProtoDate
  targetDateTo: ProtoDate
  /** area / 日付が未確定の間はクエリを無効化する。 */
  enabled?: boolean
}

/**
 * 電力エリアの実績気象 (BigQuery AMeDAS、30 分コマ集約) を取得する。
 *
 * 予報 gRPC (useAreaWeatherForecast) の実績版。バックエンドが 10 分観測を
 * 30 分コマへ集約し、気温・露点 (Magnus 算出)・降水量・実績天気を返す。
 * 再取得間隔は `WEATHER_REFETCH_INTERVAL_MS` (120 秒) に揃える (根拠は同定数の
 * JSDoc)。AMeDAS 由来の DPD 側キャッシュは 10 分更新だが、10 分間隔で揃えると
 * 更新の取りこぼしが起きるため 120 秒で回す。
 *
 * fail-safe: 気象チャートは補助表示なので、取得失敗しても error boundary
 * (全画面エラー) に飛ばさない。global QueryClient の `throwOnError: true` を
 * 本 query だけ `false` に上書きし、失敗時は data undefined → 気象チャート
 * 非表示に降格してテーブル・需要チャート本体の描画を妨げない (doji-bg AC-372)。
 */
export function useAreaWeatherActual(
  params: UseAreaWeatherActualParams,
): UseQueryResult<GetAreaWeatherActualResponse, ConnectError> {
  return useQuery(
    getAreaWeatherActual,
    create(GetAreaWeatherActualRequestSchema, {
      areaCode: params.areaCode,
      targetDateFrom: params.targetDateFrom,
      targetDateTo: params.targetDateTo,
    }),
    {
      enabled: params.enabled ?? true,
      throwOnError: false,
      refetchInterval: WEATHER_REFETCH_INTERVAL_MS,
    },
  )
}
